/**
 * Per-model XYZ transform gizmo + numeric editor for the standalone viewer.
 *
 * Lets the user manipulate a model's position / rotation / scale in three
 * complementary ways:
 *   1. Drag the 3D gizmo in the scene (TransformControls).
 *   2. Type exact numbers into the X / Y / Z inputs in the floating panel.
 *   3. Click "Reset" to restore the model's transform from the moment it
 *      was first attached (the "factory" snapshot — i.e. position right
 *      after the model finished loading and before any saved overrides
 *      were applied).
 *
 * The changes are applied directly to the THREE.Object3D — they never
 * touch the admin DB or the Mapbox app view. Persistence is handled by
 * the caller (viewer.ts mirrors them to MongoDB + localStorage).
 */

import * as THREE from "three";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";

export type TransformMode = "translate" | "rotate" | "scale";

export interface TransformVec {
  x: number;
  y: number;
  z: number;
}

export interface TransformSnapshot {
  position: TransformVec;
  rotation: TransformVec; // radians, like THREE.Euler
  scale: TransformVec;
}

export interface ModelTransformChange extends TransformSnapshot {
  modelId: string;
}

export interface ModelTransformControllerOptions {
  world: any; // OBC.SimpleWorld
  /** Called on every drag- or input-induced object change. */
  onChange?: (change: ModelTransformChange) => void;
  /** Called when the user releases the gizmo / commits an input change. */
  onChangeCommit?: (change: ModelTransformChange) => void;
}

export interface AttachOptions {
  /**
   * Snapshot of the "original" transform for this model (e.g. captured
   * by the viewer right after the model loaded but before any saved
   * override was applied). The Reset button restores this. If omitted,
   * the controller falls back to whatever the object's transform was
   * the first time it was ever attached this session.
   */
  originalTransform?: TransformSnapshot;
}

export interface ModelTransformControllerHandle {
  attach(
    modelId: string,
    object: THREE.Object3D,
    displayName?: string,
    options?: AttachOptions
  ): void;
  detach(): void;
  setMode(mode: TransformMode): void;
  getMode(): TransformMode;
  isAttached(): boolean;
  getCurrentModelId(): string | null;
  isDragging(): boolean;
  /** Restore the original transform of the currently-attached model. */
  resetCurrent(): void;
  dispose(): void;
}

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;

function snapshotOf(obj: THREE.Object3D): TransformSnapshot {
  return {
    position: { x: obj.position.x, y: obj.position.y, z: obj.position.z },
    rotation: { x: obj.rotation.x, y: obj.rotation.y, z: obj.rotation.z },
    scale: { x: obj.scale.x, y: obj.scale.y, z: obj.scale.z },
  };
}

function applySnapshot(obj: THREE.Object3D, s: TransformSnapshot): void {
  obj.position.set(s.position.x, s.position.y, s.position.z);
  obj.rotation.set(s.rotation.x, s.rotation.y, s.rotation.z);
  obj.scale.set(s.scale.x, s.scale.y, s.scale.z);
  obj.updateMatrixWorld(true);
}

export function createModelTransformController(
  opts: ModelTransformControllerOptions
): ModelTransformControllerHandle {
  const { world, onChange, onChangeCommit } = opts;
  const renderer = world.renderer.three as THREE.WebGLRenderer;
  const camera = world.camera.three as THREE.Camera;
  const scene = world.scene.three as THREE.Scene;

  const tc = new TransformControls(camera, renderer.domElement);
  try { tc.setSize(0.9); } catch { /* noop */ }
  try { tc.setSpace("world"); } catch { /* noop */ }

  // Newer three.js exposes the gizmo meshes via tc.getHelper(); fall back
  // to tc itself for older versions.
  const helper: THREE.Object3D =
    typeof (tc as any).getHelper === "function"
      ? (tc as any).getHelper()
      : (tc as unknown as THREE.Object3D);
  scene.add(helper);

  let currentModelId: string | null = null;
  let currentMode: TransformMode = "translate";
  let dragging = false;
  /**
   * Per-model "factory default" snapshots. Keyed by modelId so reattaching
   * the same model later still restores to the right baseline.
   */
  const originalsByModel = new Map<string, TransformSnapshot>();

  // Disable orbit controls during drag so the camera doesn't fight the
  // gizmo.
  tc.addEventListener("dragging-changed", (event: any) => {
    dragging = !!event.value;
    try { world.camera.controls.enabled = !dragging; } catch { /* noop */ }
    if (!dragging && currentModelId && tc.object) {
      onChangeCommit?.(snapshotChange());
    }
  });

  tc.addEventListener("objectChange", () => {
    if (!currentModelId || !tc.object) return;
    onChange?.(snapshotChange());
    syncInputsFromObject();
  });

  function snapshotChange(): ModelTransformChange {
    const o = tc.object as THREE.Object3D;
    return { modelId: currentModelId!, ...snapshotOf(o) };
  }

  // Strip clipping planes from the gizmo's materials so it never gets
  // clipped by user clipper planes. Same trick the clipper itself uses
  // for its own gizmo.
  const exemptFromClipping = (root: THREE.Object3D | null | undefined) => {
    if (!root) return;
    root.traverse((child: any) => {
      const m = child.material;
      if (!m) return;
      const apply = (mat: any) => {
        try {
          mat.clippingPlanes = [];
          mat.clipShadows = false;
          mat.depthTest = false;
        } catch { /* noop */ }
      };
      if (Array.isArray(m)) m.forEach(apply);
      else apply(m);
    });
  };
  const enforceExempt = () => {
    try {
      exemptFromClipping(helper);
      exemptFromClipping((tc as any)._root);
      exemptFromClipping((tc as any)._gizmo);
      exemptFromClipping((tc as any)._plane);
    } catch { /* noop */ }
  };
  try {
    if (world.renderer && world.renderer.onBeforeUpdate) {
      world.renderer.onBeforeUpdate.add(enforceExempt);
    }
  } catch { /* noop */ }
  enforceExempt();

  // -----------------------------------------------------------
  // Floating UI panel
  // -----------------------------------------------------------
  const panel = document.createElement("div");
  panel.style.cssText = `
    position: fixed;
    right: 16px;
    bottom: 16px;
    background: rgba(8, 18, 28, 0.94);
    border: 1px solid rgba(68, 211, 222, 0.4);
    border-radius: 8px;
    color: #cfeef0;
    font-family: ui-sans-serif, system-ui, sans-serif;
    font-size: 12px;
    padding: 10px 12px;
    display: none;
    z-index: 10000;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
    user-select: none;
    min-width: 280px;
  `;
  const panelLabel = document.createElement("div");
  panelLabel.style.cssText =
    "margin-bottom: 8px; font-weight: 600; color: #44d3de;";
  panel.appendChild(panelLabel);

  const btnRow = document.createElement("div");
  btnRow.style.cssText =
    "display: flex; gap: 4px; align-items: center; margin-bottom: 8px;";
  panel.appendChild(btnRow);

  const makeBtn = (label: string, title: string) => {
    const b = document.createElement("button");
    b.textContent = label;
    b.title = title;
    b.style.cssText = `
      background: rgba(68, 211, 222, 0.15);
      color: #cfeef0;
      border: 1px solid rgba(68, 211, 222, 0.35);
      border-radius: 4px;
      padding: 4px 8px;
      font-size: 11px;
      cursor: pointer;
    `;
    b.onmouseenter = () => { b.style.background = "rgba(68, 211, 222, 0.30)"; };
    b.onmouseleave = () => { b.style.background = "rgba(68, 211, 222, 0.15)"; };
    return b;
  };
  const btnTranslate = makeBtn("Move", "Translate along X / Y / Z");
  const btnRotate = makeBtn("Rotate", "Rotate around X / Y / Z");
  const btnScale = makeBtn("Scale", "Scale along X / Y / Z");
  const btnSpace = makeBtn("World", "Toggle World / Local axis space");
  const btnReset = makeBtn("Reset", "Restore original position / rotation / scale");
  btnReset.style.borderColor = "rgba(255, 200, 80, 0.5)";
  btnReset.style.background = "rgba(255, 200, 80, 0.12)";
  btnReset.onmouseenter = () => {
    btnReset.style.background = "rgba(255, 200, 80, 0.28)";
  };
  btnReset.onmouseleave = () => {
    btnReset.style.background = "rgba(255, 200, 80, 0.12)";
  };
  const btnDone = makeBtn("Done", "Detach the gizmo");
  btnDone.style.borderColor = "rgba(255, 110, 110, 0.5)";
  btnDone.style.background = "rgba(255, 110, 110, 0.15)";
  btnDone.onmouseenter = () => {
    btnDone.style.background = "rgba(255, 110, 110, 0.30)";
  };
  btnDone.onmouseleave = () => {
    btnDone.style.background = "rgba(255, 110, 110, 0.15)";
  };
  btnRow.appendChild(btnTranslate);
  btnRow.appendChild(btnRotate);
  btnRow.appendChild(btnScale);
  btnRow.appendChild(btnSpace);
  btnRow.appendChild(btnReset);
  btnRow.appendChild(btnDone);

  // ---- Numeric input grid -------------------------------------------------
  // 4 columns: label | X | Y | Z. 3 rows for Position / Rotation / Scale.
  const grid = document.createElement("div");
  grid.style.cssText = `
    display: grid;
    grid-template-columns: 60px 1fr 1fr 1fr;
    gap: 4px 6px;
    align-items: center;
  `;
  panel.appendChild(grid);

  type Row = "position" | "rotation" | "scale";
  type Axis = "x" | "y" | "z";
  const inputs: Record<Row, Record<Axis, HTMLInputElement>> = {
    position: {} as any,
    rotation: {} as any,
    scale: {} as any,
  };

  const makeInput = (row: Row, axis: Axis): HTMLInputElement => {
    const inp = document.createElement("input");
    inp.type = "number";
    inp.step = row === "rotation" ? "1" : row === "scale" ? "0.01" : "0.1";
    inp.style.cssText = `
      width: 100%;
      box-sizing: border-box;
      background: rgba(0, 0, 0, 0.35);
      border: 1px solid rgba(68, 211, 222, 0.25);
      border-radius: 3px;
      color: #e8f7f8;
      font-family: ui-monospace, monospace;
      font-size: 11px;
      padding: 3px 5px;
      text-align: right;
    `;
    inp.addEventListener("focus", () => {
      inp.style.border = "1px solid rgba(68, 211, 222, 0.7)";
    });
    inp.addEventListener("blur", () => {
      inp.style.border = "1px solid rgba(68, 211, 222, 0.25)";
    });
    inp.addEventListener("input", () => onInputEdit(row, axis, false));
    inp.addEventListener("change", () => onInputEdit(row, axis, true));
    return inp;
  };

  const labelStyle =
    "color: #8ad7dd; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em;";
  const axisHeader = (text: string, color: string) => {
    const d = document.createElement("div");
    d.textContent = text;
    d.style.cssText = `text-align: center; font-size: 10px; color: ${color}; font-weight: 700;`;
    return d;
  };
  // Header row
  const corner = document.createElement("div");
  grid.appendChild(corner);
  grid.appendChild(axisHeader("X", "#ff6b6b"));
  grid.appendChild(axisHeader("Y", "#7bd86c"));
  grid.appendChild(axisHeader("Z", "#5aa2ff"));

  const rowLabels: Record<Row, string> = {
    position: "Pos (m)",
    rotation: "Rot (°)",
    scale: "Scale",
  };
  (Object.keys(rowLabels) as Row[]).forEach((row) => {
    const lbl = document.createElement("div");
    lbl.textContent = rowLabels[row];
    lbl.style.cssText = labelStyle;
    grid.appendChild(lbl);
    (["x", "y", "z"] as Axis[]).forEach((axis) => {
      const inp = makeInput(row, axis);
      inputs[row][axis] = inp;
      grid.appendChild(inp);
    });
  });

  document.body.appendChild(panel);

  // ---- Input ↔ Object syncing ---------------------------------------------
  let suppressInputEvents = false;

  const syncInputsFromObject = () => {
    if (!tc.object) return;
    const o = tc.object;
    suppressInputEvents = true;
    try {
      inputs.position.x.value = o.position.x.toFixed(3);
      inputs.position.y.value = o.position.y.toFixed(3);
      inputs.position.z.value = o.position.z.toFixed(3);
      inputs.rotation.x.value = (o.rotation.x * RAD_TO_DEG).toFixed(2);
      inputs.rotation.y.value = (o.rotation.y * RAD_TO_DEG).toFixed(2);
      inputs.rotation.z.value = (o.rotation.z * RAD_TO_DEG).toFixed(2);
      inputs.scale.x.value = o.scale.x.toFixed(4);
      inputs.scale.y.value = o.scale.y.toFixed(4);
      inputs.scale.z.value = o.scale.z.toFixed(4);
    } finally {
      suppressInputEvents = false;
    }
  };

  const onInputEdit = (row: Row, axis: Axis, commit: boolean) => {
    if (suppressInputEvents) return;
    if (!currentModelId || !tc.object) return;
    const raw = inputs[row][axis].value.trim();
    if (raw === "" || raw === "-" || raw === "+") return; // mid-typing
    const num = Number(raw);
    if (!Number.isFinite(num)) return;
    const o = tc.object;
    if (row === "position") {
      o.position[axis] = num;
    } else if (row === "rotation") {
      // Rotation typed in degrees for friendliness; stored as radians.
      o.rotation[axis] = num * DEG_TO_RAD;
    } else if (row === "scale") {
      // Guard against scale=0 which collapses geometry.
      o.scale[axis] = num === 0 ? 1e-6 : num;
    }
    o.updateMatrixWorld(true);
    onChange?.(snapshotChange());
    if (commit) onChangeCommit?.(snapshotChange());
  };

  // ---- Active mode / space buttons ---------------------------------------
  const paintActiveButton = () => {
    const map: Record<TransformMode, HTMLButtonElement> = {
      translate: btnTranslate,
      rotate: btnRotate,
      scale: btnScale,
    };
    for (const m of Object.keys(map) as TransformMode[]) {
      const b = map[m];
      if (m === currentMode) {
        b.style.background = "rgba(68, 211, 222, 0.55)";
        b.style.color = "#0a0e27";
      } else {
        b.style.background = "rgba(68, 211, 222, 0.15)";
        b.style.color = "#cfeef0";
      }
    }
  };

  const setModeInternal = (mode: TransformMode) => {
    currentMode = mode;
    try { tc.setMode(mode); } catch { /* noop */ }
    enforceExempt();
    paintActiveButton();
  };

  btnTranslate.addEventListener("click", () => setModeInternal("translate"));
  btnRotate.addEventListener("click", () => setModeInternal("rotate"));
  btnScale.addEventListener("click", () => setModeInternal("scale"));
  btnSpace.addEventListener("click", () => {
    const next = (tc as any).space === "world" ? "local" : "world";
    try { tc.setSpace(next); } catch { /* noop */ }
    btnSpace.textContent = next === "world" ? "World" : "Local";
  });
  btnReset.addEventListener("click", () => resetCurrent());
  btnDone.addEventListener("click", () => detach());

  const showPanel = (displayName: string) => {
    panelLabel.textContent = `Editing: ${displayName}`;
    panel.style.display = "block";
    paintActiveButton();
    syncInputsFromObject();
  };
  const hidePanel = () => {
    panel.style.display = "none";
  };

  // -----------------------------------------------------------
  // Public API
  // -----------------------------------------------------------
  const attach = (
    modelId: string,
    object: THREE.Object3D,
    displayName?: string,
    options?: AttachOptions
  ) => {
    currentModelId = modelId;

    // Record the "factory" baseline the first time we ever see this model
    // (or refresh it if the caller passed an explicit one — that wins so
    // viewer.ts can ensure the baseline is the truly-original load-time
    // transform, captured BEFORE any saved overrides were applied).
    if (options?.originalTransform) {
      originalsByModel.set(modelId, options.originalTransform);
    } else if (!originalsByModel.has(modelId)) {
      originalsByModel.set(modelId, snapshotOf(object));
    }

    try {
      tc.attach(object);
      tc.setMode(currentMode);
    } catch (err) {
      console.warn("[modelTransform] attach failed", err);
      currentModelId = null;
      return;
    }
    enforceExempt();
    showPanel(displayName || modelId);
  };

  const detach = () => {
    try { tc.detach(); } catch { /* noop */ }
    currentModelId = null;
    hidePanel();
    try { world.camera.controls.enabled = true; } catch { /* noop */ }
  };

  const resetCurrent = () => {
    if (!currentModelId || !tc.object) return;
    const baseline = originalsByModel.get(currentModelId);
    if (!baseline) {
      console.warn(
        `[modelTransform] no baseline stored for ${currentModelId} — reset skipped`
      );
      return;
    }
    applySnapshot(tc.object as THREE.Object3D, baseline);
    syncInputsFromObject();
    onChange?.(snapshotChange());
    onChangeCommit?.(snapshotChange());
    console.log(`↩️ [modelTransform] ${currentModelId} reset to original`);
  };

  return {
    attach,
    detach,
    setMode: setModeInternal,
    getMode: () => currentMode,
    isAttached: () => !!currentModelId,
    getCurrentModelId: () => currentModelId,
    isDragging: () => dragging,
    resetCurrent,
    dispose() {
      try { tc.detach(); } catch { /* noop */ }
      try { (tc as any).dispose?.(); } catch { /* noop */ }
      try { scene.remove(helper); } catch { /* noop */ }
      try {
        if (world.renderer && world.renderer.onBeforeUpdate) {
          world.renderer.onBeforeUpdate.remove(enforceExempt);
        }
      } catch { /* noop */ }
      panel.remove();
    },
  };
}
